import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Users, 
  Building2, 
  Database, 
  Activity,
  HardDrive,
  Zap,
  AlertCircle
} from 'lucide-react';
import Card from '../../components/common/Card';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import api from '../../services/api';

interface PlatformStats {
  total_organizations: number;
  total_users: number;
  active_users_30d: number;
  total_missions: number;
  total_storage_gb: number;
  total_api_calls_30d: number;
  avg_response_time_ms: number;
  error_rate_percent: number;
}

interface OrganizationUsage {
  organization_id: string;
  organization_name: string;
  user_count: number;
  mission_count: number;
  storage_gb: number;
  api_calls_30d: number;
  last_activity: string;
}

interface ResourceMetrics {
  cpu_usage_percent: number;
  memory_usage_percent: number;
  disk_usage_percent: number;
  active_connections: number;
}

export default function PlatformAnalyticsPage() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [topOrgs, setTopOrgs] = useState<OrganizationUsage[]>([]);
  const [resources, setResources] = useState<ResourceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [statsRes, orgsRes, resourcesRes] = await Promise.all([
        api.get('/organizations/analytics/platform-stats'),
        api.get('/organizations/analytics/top-organizations', { params: { limit: 10 } }),
        api.get('/organizations/analytics/resource-usage'),
      ]);
      
      setStats(statsRes.data.data);
      setTopOrgs(orgsRes.data.data);
      setResources(resourcesRes.data.data);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.response?.data?.error || err.message || 'Failed to load analytics';
      console.error('Analytics fetch error:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatBytes = (gb: number): string => {
    if (gb >= 1000) return `${(gb / 1000).toFixed(1)} TB`;
    return `${gb.toFixed(1)} GB`;
  };

  const getUsageColor = (percent: number): string => {
    if (percent >= 90) return 'text-red-600 dark:text-red-400';
    if (percent >= 75) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Platform Analytics
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Cross-organization metrics and resource usage monitoring
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert type="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Key Metrics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Organizations</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatNumber(stats.total_organizations)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Building2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatNumber(stats.total_users)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formatNumber(stats.active_users_30d)} active
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                <Users className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Missions</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatNumber(stats.total_missions)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Database className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Storage Used</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatBytes(stats.total_storage_gb)}
                </p>
              </div>
              <div className="p-3 bg-amber-100 dark:bg-amber-900 rounded-lg">
                <HardDrive className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Performance Metrics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <div className="flex items-center gap-3">
              <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <div className="flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">API Calls (30d)</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(stats.total_api_calls_30d)}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-3">
              <Activity className="w-6 h-6 text-green-600 dark:text-green-400" />
              <div className="flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg Response Time</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.avg_response_time_ms}ms
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-3">
              <AlertCircle className={`w-6 h-6 ${stats.error_rate_percent > 5 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`} />
              <div className="flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Error Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.error_rate_percent.toFixed(2)}%
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Resource Usage */}
      {resources && (
        <Card>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            System Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">CPU Usage</span>
                <span className={`text-sm font-semibold ${getUsageColor(resources.cpu_usage_percent)}`}>
                  {resources.cpu_usage_percent.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    resources.cpu_usage_percent >= 90 ? 'bg-red-600' :
                    resources.cpu_usage_percent >= 75 ? 'bg-yellow-600' :
                    'bg-green-600'
                  }`}
                  style={{ width: `${resources.cpu_usage_percent}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Memory Usage</span>
                <span className={`text-sm font-semibold ${getUsageColor(resources.memory_usage_percent)}`}>
                  {resources.memory_usage_percent.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    resources.memory_usage_percent >= 90 ? 'bg-red-600' :
                    resources.memory_usage_percent >= 75 ? 'bg-yellow-600' :
                    'bg-green-600'
                  }`}
                  style={{ width: `${resources.memory_usage_percent}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Disk Usage</span>
                <span className={`text-sm font-semibold ${getUsageColor(resources.disk_usage_percent)}`}>
                  {resources.disk_usage_percent.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    resources.disk_usage_percent >= 90 ? 'bg-red-600' :
                    resources.disk_usage_percent >= 75 ? 'bg-yellow-600' :
                    'bg-green-600'
                  }`}
                  style={{ width: `${resources.disk_usage_percent}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Active Connections</span>
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                  {resources.active_connections}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Database connections
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Top Organizations */}
      <Card>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          Top Organizations by Usage
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                  Organization
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                  Users
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                  Missions
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                  Storage
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                  API Calls
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">
                  Last Activity
                </th>
              </tr>
            </thead>
            <tbody>
              {topOrgs.map((org, index) => (
                <tr
                  key={org.organization_id}
                  className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        #{index + 1}
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {org.organization_name}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-900 dark:text-white">
                    {formatNumber(org.user_count)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-900 dark:text-white">
                    {formatNumber(org.mission_count)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-900 dark:text-white">
                    {formatBytes(org.storage_gb)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-900 dark:text-white">
                    {formatNumber(org.api_calls_30d)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-600 dark:text-gray-400">
                    {new Date(org.last_activity).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Made with Bob
